import { value } from '@takeshape/util';
import { updateAuthentication } from "../util/authentication.js";
import { isServiceConfig } from "../util/config.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
import { getInitialData } from "../util/initial-data.js";
export const name = 'portkey';
const title = 'Portkey';
/** Portkey's AI gateway, an OpenAI-compatible endpoint. */
export const endpoint = 'https://api.portkey.ai/v1';
export const apiKeyHeader = 'x-portkey-api-key';
export const providerHeader = 'x-portkey-provider';
export const configHeader = 'x-portkey-config';
export const provider = {
    name,
    title,
    description: 'Portkey is an AI gateway that routes requests to hundreds of models through a single OpenAI-compatible API, adding observability, caching, routing and guardrails.',
    categories: ['ai'],
    links: [
        { title: 'Homepage', url: 'https://portkey.ai/' },
        { title: 'Docs', url: '/docs/services/providers/portkey' }
    ],
    // Portkey only implements part of the OpenAI API — the Assistants, Threads and
    // Moderations routes aren't handled by the gateway at all, and which of the
    // remaining routes work depends on the provider it's configured to route to.
    // There's no spec worth introspecting, so this is an LLM-only service, used
    // through agents and the AI resolvers rather than as a GraphQL data source.
    serviceType: 'rest',
    createId: value(name),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'providerSlug', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'Portkey'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'portkey'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'Portkey'
            },
            providerSlug: {
                title: 'Provider Slug',
                type: 'string'
            },
            configId: {
                title: 'Config ID',
                type: 'string'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    token: {
                        title: 'API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'providerSlug', 'configId', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            providerSlug: {
                widget: 'singleLineText',
                instructions: 'The Portkey provider to route requests to, for example `@takeshape-agent`. You can manage providers at https://app.portkey.ai/model-catalog'
            },
            configId: {
                widget: 'singleLineText',
                instructions: 'An optional Portkey config to attach to every request, for example a config that applies Prisma AIRS guardrails. You can manage configs at https://app.portkey.ai/configs'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'You can manage API keys for Portkey at https://app.portkey.ai/api-keys'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const headers = {};
        if (typeof formData.providerSlug === 'string') {
            headers[providerHeader] = formData.providerSlug;
        }
        if (typeof formData.configId === 'string' && formData.configId !== '') {
            headers[configHeader] = formData.configId;
        }
        const config = {
            title: formData.title ?? title,
            namespace: formData.namespace ?? title,
            provider: name,
            serviceType: 'rest',
            authenticationType: 'bearer',
            options: {
                endpoint,
                headers
            }
        };
        return updateAuthentication(config, formData, {
            type: 'bearer',
            header: apiKeyHeader
        });
    },
    getInitialData(serviceConfig) {
        const initialData = getInitialData(provider, serviceConfig);
        const headers = serviceConfig?.options?.headers;
        if (headers) {
            initialData.providerSlug = headers[providerHeader];
            initialData.configId = headers[configHeader];
        }
        return initialData;
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', `options.headers.${providerHeader}`]),
    isServiceConfig: isServiceConfig(name, [
        'authentication.token',
        'options.endpoint',
        `options.headers.${providerHeader}`
    ]),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/models')
    }),
    getAiModels() {
        // The available models depend entirely on the provider Portkey is configured
        // to route to, so there's no static list to offer. Model fields accept
        // free-form input.
        return {
            generate: [],
            embedding: [],
            image: []
        };
    }
};
export default provider;
